import { createTheme } from '@mui/material/styles';
import palette from './palette';

const { spacing } = createTheme();

/**
 * @note Please keep in alphabetical order
 */
const components = {
  MuiAccordion: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        '&.Mui-expanded:before': { opacity: 1 },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        paddingTop: 0,
        '& .MuiListItem-root': { paddingTop: 0 },
      },
      content: {
        fontSize: 14,
        marginTop: 20,
        marginBottom: 20,
      },
      expandIconWrapper: {
        transition: 'none',
        '& svg': { fontSize: 14 },
        '.collapseIcon': { display: 'none' },

        '&.Mui-expanded': {
          '.collapseIcon': { display: 'block' },
          '.expandIcon': { display: 'none' },
        },
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        color: palette.ink.main,
        width: '100%',
      },
      icon: {
        padding: '11px 0',
        '& .MuiSvgIcon-root': { fontSize: 14 },
      },
      standardInfo: {
        backgroundColor: '#ECF5FB',
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiFilledInput-root': { padding: '0 16px' },
      },
      inputRoot: { paddingTop: '5px' },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: 16,
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        margin: '1rem 0',
        padding: '0.625rem 1.25rem',
        borderRadius: 28,
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1.15,
        textTransform: 'none' as const,
        '&:disabled, &.Mui-disabled': {
          pointerEvents: 'all !important',
          cursor: 'not-allowed', // has no effect with :disabled, due to pointer-events: none
          opacity: '25%',
        },
        '&.MuiLoadingButton-loading': {
          cursor: 'not-allowed',
          opacity: 1,
        },
        '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
          fontSize: 14,
        },
      },
      text: {
        color: palette.primary.dark,
        '&:hover, &:focus': {
          color: palette.primary.main,
          backgroundColor: palette.primary.light,
        },
        '&:active': {
          color: palette.primary.dark,
          backgroundColor: palette.primary.light,
        },
        '&:disabled': { color: palette.ink.main },
        '& .MuiLoadingButton-loadingIndicator': {
          color: palette.ink.main,
          fill: palette.ink.main,
        },
      },
      textSecondary: {
        color: '#333333', // TBD?: palette.secondary.main
      },
      textSuccess: { color: palette.success.main },
      textWarning: { color: palette.warning.main },
      textError: { color: palette.error.main },
      textInk: { color: palette.ink.main },
      textSky: { color: palette.sky.main },
      textGhost: { color: palette.ghost.main },
      outlined: {
        fontSize: 14,
        color: palette.primary.dark,
        borderColor: palette.primary.dark,
        '&:hover, &:focus': {
          color: palette.primary.main,
          backgroundColor: 'transparent',
        },
        '&:active': {
          color: palette.primary.dark,
          borderColor: palette.primary.dark,
          backgroundColor: palette.primary.light,
        },
        '& .MuiLoadingButton-loadingIndicator': {
          color: palette.ink.main,
          fill: palette.ink.main,
        },
      },
      outlinedSecondary: {
        color: '#333333', // TBD?: palette.secondary.main
        borderColor: '#333333', // TBD?: palette.secondary.main
      },
      outlinedSuccess: {
        color: palette.success.main,
        borderColor: palette.success.main,
        '&:hover, &:focus': {
          color: palette.success.main,
          borderColor: palette.success.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
      outlinedWarning: {
        color: palette.warning.main,
        borderColor: palette.warning.main,
        '&:hover, &:focus': {
          color: palette.warning.main,
          borderColor: palette.warning.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
      outlinedError: {
        color: palette.error.main,
        borderColor: palette.error.main,
        '&:hover, &:focus': {
          color: palette.error.main,
          borderColor: palette.error.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
      outlinedInk: {
        color: palette.ink.main,
        borderColor: palette.ink.main,
        '&:hover, &:focus': {
          color: palette.ink.main,
          borderColor: palette.ink.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
      outlinedSky: {
        color: palette.sky.main,
        borderColor: palette.sky.main,
        '&:hover, &:focus': {
          color: palette.sky.main,
          borderColor: palette.sky.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
      outlinedGhost: {
        color: palette.ghost.main,
        borderColor: palette.ghost.main,
        '&:hover, &:focus': {
          color: palette.ghost.main,
          borderColor: palette.ghost.main,
          opacity: '80%',
        },
        '&:active': { backgroundColor: 'transparent', opacity: 1 },
      },
    },
    variants: [
      {
        props: { variant: 'flat' },
        style: {
          backgroundColor: palette.primary.dark,
          color: 'white',
          '& .MuiLoadingButton-loadingIndicator': {
            color: 'white',
            fill: 'white',
          },
        },
      },
      {
        props: { variant: 'flat', color: 'primary' },
        style: {
          '&:hover, &:focus': { backgroundColor: palette.primary.main },
          '&:active': { backgroundColor: '#009B43' }, // TBD: Squeeze #009B43 into primary color palette
        },
      },
      {
        props: { variant: 'flat', color: 'secondary' },
        style: {
          backgroundColor: '#333333', // TBD: palette.secondary.dark
          // TBD: '&:hover, &:focus': { backgroundColor: palette.secondary.main },
          // TBD: active state
        },
      },
      {
        props: { variant: 'flat', color: 'success' },
        style: {
          backgroundColor: palette.success.main,
          // TBD: hover, focus, & active states
        },
      },
      {
        props: { variant: 'flat', color: 'warning' },
        style: {
          backgroundColor: palette.warning.main,
          '&:hover, &:focus': { backgroundColor: palette.warning.main },
          '&:active': { backgroundColor: '#E58714' },
        },
      },
      {
        props: { variant: 'flat', color: 'error' },
        style: {
          backgroundColor: palette.error.main,
          '&:hover, &:focus': { backgroundColor: '#FF5955' },
          '&:active': { backgroundColor: '#CE4542' },
        },
      },
      {
        props: { variant: 'flat', color: 'info' },
        style: {
          backgroundColor: palette.info.dark,
          '&:hover, &:focus': { backgroundColor: palette.info.main },
          // TBD: active
        },
      },
      {
        props: { variant: 'flat', color: 'ink' },
        style: {
          backgroundColor: palette.ink.main,
          // TBD: hover, focus, & active states
        },
      },
      {
        props: { variant: 'flat', color: 'sky' },
        style: {
          backgroundColor: palette.sky.main,
          color: palette.sky.contrastText,
          // TBD: hover, focus, & active states
        },
      },
      {
        props: { variant: 'flat', color: 'ghost' },
        style: {
          backgroundColor: palette.ghost.dark,
          '&:hover, &:focus': { backgroundColor: palette.ghost.main },
          // TBD: active state
        },
      },
      {
        props: { variant: 'uncontained' },
        style: {
          color: palette.primary.dark,
          backgroundColor: 'transparent',
          textTransform: 'none',
          '&:hover, &:focus': {
            color: palette.primary.main,
            backgroundColor: 'transparent',
          },
          '&:active': {
            color: palette.primary.dark,
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
          '&:disabled': { color: palette.ink.main },
          '& .MuiLoadingButton-loadingIndicator': {
            color: palette.ink.main,
            fill: palette.ink.main,
          },
        },
      },
      {
        props: { variant: 'uncontained', color: 'secondary' },
        style: {
          color: '#333333', // TBD: palette.secondary.main,
          '&:active': {
            color: '#333333', // TBD: palette.secondary.main,
            textDecorationColor: '#333333', // TBD: palette.secondary.main,
          },
        },
      },
      {
        props: { variant: 'uncontained', color: 'success' },
        style: {
          color: palette.success.main,
          '&:hover, &:focus': { color: palette.success.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'uncontained', color: 'warning' },
        style: {
          color: palette.warning.main,
          '&:hover, &:focus': { color: palette.warning.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'uncontained', color: 'error' },
        style: {
          color: palette.error.main,
          '&:hover, &:focus': { color: palette.error.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'uncontained', color: 'ink' },
        style: {
          color: palette.ink.main,
          '&:hover, &:focus': { color: palette.ink.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'uncontained', color: 'sky' },
        style: {
          color: palette.sky.main,
          '&:hover, &:focus': { color: palette.sky.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'uncontained', color: 'ghost' },
        style: {
          color: palette.ghost.main,
          '&:hover, &:focus': { color: palette.ghost.main, opacity: '80%' },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'floated' },
        style: {
          color: '#808080',
          backgroundColor: 'white',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        },
      },
      {
        props: { variant: 'floated', color: 'primary' },
        style: {
          '&:hover, &:focus': {
            color: palette.primary.main,
            backgroundColor: palette.primary.light,
          },
          '&:active': {
            color: palette.primary.dark,
            backgroundColor: palette.primary.light,
          },
          '& .MuiLoadingButton-loadingIndicator': {
            color: palette.ink.main,
            fill: palette.ink.main,
          },
        },
      },
      {
        props: { variant: 'floated', color: 'success' },
        style: {
          color: palette.success.main,
          '&:hover, &:focus': {
            color: palette.success.main,
            backgroundColor: 'transparent',
            opacity: '80%',
          },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'floated', color: 'warning' },
        style: {
          color: palette.warning.main,
          '&:hover, &:focus': {
            color: palette.warning.main,
            backgroundColor: 'transparent',
            opacity: '80%',
          },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'floated', color: 'error' },
        style: {
          color: palette.error.main,
          '&:hover, &:focus': {
            color: palette.error.main,
            backgroundColor: 'transparent',
            opacity: '80%',
          },
          '&:active': { opacity: 1 },
        },
      },
      {
        props: { variant: 'link' },
        style: {
          display: 'inline-flex',
          margin: 0,
          fontSize: 'inherit',
          lineHeight: 'inherit',
          color: 'inherit',
          fontWeight: 400,
          textDecoration: 'underline',
          verticalAlign: 'baseline',
          padding: 0,

          '&:hover, &:focus': {
            color: palette.primary.main,
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
          '&:active': {
            color: palette.primary.dark,
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
          '&:disabled': { opacity: 0.25 },
        },
      },
      {
        props: { variant: 'link', color: 'primary' },
        style: {
          display: 'inline-flex',
          margin: 0,
          fontSize: 'inherit',
          lineHeight: 'inherit',
          color: palette.primary.dark,
          fontWeight: 400,
          textDecoration: 'underline',
          verticalAlign: 'baseline',
          padding: 0,

          '&:hover, &:focus': {
            color: palette.primary.main,
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
          '&:active': {
            color: palette.primary.dark,
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
          '&:disabled': { opacity: 0.25 },
        },
      },
    ],
  },

  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },

  MuiButtonGroup: {
    styleOverrides: {
      grouped: {
        margin: 0,
      },
    },
  },

  MuiCard: {
    defaultProps: {
      raised: true,
    },
    styleOverrides: {
      root: {
        border: '0.5px solid #D9D9D9',
        boxShadow: '0px 4px 14px #0000000D',
        borderRadius: 20,
      },
    },
    variants: [
      {
        props: { role: 'button' },
        style: {
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0px 4px 14px 0px #00000026',
          },
        },
      },
    ],
  },

  MuiCardContent: {
    styleOverrides: {
      root: { padding: 24 },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: 24,
        '& + .MuiCardContent-root': { paddingTop: 8 }, // 32px between titles & content
      },
      title: {
        fontSize: 16,
        fontWeight: 600,
        color: '#333333',
      },
      subheader: {
        marginTop: 8,
        fontSize: 14,
        color: '#808080',
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        // This sizes the path element to 14px
        svg: { fontSize: 18.75 },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: 13,
        height: 28,
        borderRadius: 10,
      },
      icon: {
        fontSize: 12,
        height: 12,
        width: 12,
        marginLeft: 10,
        marginRight: -4,
      },
      filled: {
        fontWeight: 700,
        color: palette.ink.main,
        backgroundColor: '#E5E5E573',
      },
      filledPrimary: {
        color: palette.primary.dark,
        backgroundColor: palette.primary.light,
      },
      filledSecondary: {},
      filledSuccess: {
        color: '#008439',
        backgroundColor: '#F3FAF3',
      },
      filledWarning: {
        color: '#E07505',
        backgroundColor: 'rgba(255, 157, 0, 0.1)',
      },
      filledError: {
        color: palette.error.main,
        backgroundColor: 'rgba(229, 57, 53, 0.04)',
      },
      filledInfo: {
        color: '#0095E5',
        backgroundColor: '#ECF5FB',
      },
      outlined: {
        color: '#808080',
        backgroundColor: 'white',
        border: '1px solid rgba(217, 217, 217, 0.5)',
        '& > .MuiChip-label + .MuiSvgIcon-root': {
          fontSize: 8,
          marginRight: 12,
          cursor: 'pointer',
        },
      },
      sizeSmall: {
        fontSize: 11,
        height: 20,
        borderRadius: 6,
      },
      labelSmall: {
        padding: '0 12px',
      },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: {
        fontFamily: 'Mulish',
        borderCollapse: 'separate',
        a: {
          color: palette.primary.dark,
          textDecoration: 'underline',
          textDecorationColor: palette.primary.dark,
          '&:hover, &:focus': {
            color: palette.primary.main,
            textDecoration: 'underline',
            textDecorationColor: palette.primary.main,
          },
        },
        '.MuiTableHead-root': {
          '& .MuiTableCell-root': {
            textTransform: 'uppercase',
            fontWeight: 700,
            textAlign: 'left',
          },
          '.MuiTableRow-root': {
            height: '1rem',
            boxShadow: 'none',
          },
        },
        '.MuiTableBody-root': {
          borderRadius: '20px',
          boxShadow: '0 4px 30px rgba(18, 49, 43, 0.05)',
        },
        '.MuiTableRow-root': {
          height: '3rem',
        },
        '.MuiTableRow-subrow': {
          backgroundColor: '#FAFAFA',
        },
        '.MuiTableCell-root': {
          padding: '0 0 0 1rem',
          border: 'none',
        },
        th: {
          paddingLeft: '1rem',
        },
      },
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      columnHeader: {
        '&:focus, &:focus-within': { outline: 'none' },
        '&.MuiDataGrid-columnHeader--selectAll': {
          padding: 0,
          marginLeft: -16,
          marginRight: 16,
          '.MuiDataGrid-columnSeparator': { display: 'none' },
        },

        // Hide the column separator between the checkbox column and the selectAll one
        '&:has(+ .MuiDataGrid-columnHeader--selectAll) .MuiDataGrid-columnSeparator':
          { display: 'none' },
      },
      columnHeaderTitle: {
        fontWeight: 700,
        '&:focus, &:focus-within': { outline: 'none' },
      },
      panelWrapper: {
        padding: 12,
        '& .MuiInputBase-root': { marginTop: 0 },
      },
      columnsPanelRow: {
        '.MuiFormControlLabel-root': {
          width: '100%',
          flexDirection: 'row-reverse',
          justifyContent: 'space-between',
        },
      },
      overlayWrapperInner: {
        minHeight: '18rem', // aligns with MuiDataGrid-virtualScroller in DataGridThrive
        overflowY: 'auto',
      },
      row: {
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        '&.Mui-odd:not(.Mui-selected)': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: `${palette.primary.main}14`, // 8% opacity
            '&:hover': { backgroundColor: `${palette.primary.main}0A` }, // 4% opacity
          },
        },
      },
      cell: {
        '&:focus, &:focus-within': { outline: 'none' },
        '&.MuiDataGrid-cell--editable': {
          backgroundColor: palette.primary.light,
          '& > input': {
            width: '100%',
            border: 'none',
            textAlign: 'right',
          },
        },
      },
      editInputCell: {
        '&.Mui-error': { backgroundColor: palette.error.light },
      },
      selectedRowCount: {
        pointerEvents: 'none',

        ':after': {
          pointerEvents: 'all',
          /**
           * content value can be added by components
           * @note MUI styles require nested quotes for content
           */
          // content: '"Show only selected"',
          marginLeft: 4,
          color: palette.primary.dark,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        },
      },

      root: {
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        '.MuiDataGrid-cell--textRight': { textAlign: 'right' }, // ensures that text in child components is aligned

        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell--withRenderer': {
          '& .MuiInputBase-root': { height: 32 },
        },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        minWidth: 510,
        maxHeight: 'calc(100% - 100px)',
        borderRadius: '1.25rem',
      },
    },
    variants: [
      {
        props: { variant: 'text-with-icons' },
        style: {
          '.MuiPaper-root > .MuiBox-root': {
            svg: {
              marginLeft: -3,
            },
          },
          '.MuiDialogContent-root': {
            '.MuiBox-root': {
              span: {
                marginLeft: 12,
              },
            },
          },
        },
      },
      {
        props: { variant: 'text-with-icons', color: 'primary' },
        style: {
          '.MuiPaper-root > .MuiBox-root': {
            backgroundColor: `${palette.primary.main}10`,
            svg: {
              color: palette.primary.dark,
            },
          },
          '.MuiDialogContent-root': {
            '.MuiBox-root': {
              svg: {
                color: palette.warning.main,
              },
            },
          },
        },
      },
      {
        props: { variant: 'text-with-icons', color: 'error' },
        style: {
          '.MuiPaper-root > .MuiBox-root': {
            backgroundColor: `${palette.error.main}10`,
            svg: {
              color: palette.error.main,
            },
          },
          '.MuiDialogContent-root': {
            '.MuiBox-root': {
              svg: {
                color: palette.error.main,
              },
            },
          },
        },
      },
    ],
  },

  MuiDialogActions: {
    styleOverrides: {
      root: { padding: '24px 48px' },
    },
  },

  MuiFilledInput: {
    styleOverrides: {
      root: {
        height: 48,
        borderRadius: '24px',
        paddingTop: 'auto',
        ':before, :after': { border: 'none !important' },
      },
      input: { padding: 8 },
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: {
        '&.MuiTelInput-TextField': { marginTop: 12 },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: '4px',
        '&[color=error]': { color: palette.error.main },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        color: palette.ghost.dark,
      },
      primary: {
        color: palette.ink.main,
        '&:hover': {
          color: palette.primary.main,
          backgroundColor: palette.primary.light,
        },
        '&:active': {
          color: palette.primary.dark,
        },
        '&:disabled': {
          opacity: '25%',
          // cursor: 'not-allowed', // has no effect, due to pointer-events: none
        },
      },
    },
    variants: [
      {
        props: { variant: 'floated' },
        style: {
          backgroundColor: 'white',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          '&:hover, &:focus': {
            color: palette.primary.main,
            backgroundColor: palette.primary.light,
          },
          '&:active': {
            color: palette.primary.dark,
            backgroundColor: palette.primary.light,
          },
          '&:disabled': { backgroundColor: 'white' },
        },
      },
      {
        props: { variant: 'hover-alert' },
        style: {
          '&:hover, &:focus': {
            color: '#a02725',
            backgroundColor: `${palette.error.main}10`,
          },
        },
      },
    ],
  },

  MuiInputAdornment: {
    styleOverrides: {
      filled: { marginTop: '2px !important' },
    },
  },

  MuiInputBase: {
    styleOverrides: {
      root: {
        '&:not(.MuiInputBase-multiline)': { height: 48 },
        '& .MuiIconButton-root': { color: palette.ink.main },
      },
      input: {
        '&::placeholder': {
          color: palette.text.secondary,
          opacity: 1,
        },
      },
      inputMultiline: {
        padding: '0 !important',
      },
      inputAdornedEnd: {
        // TBD: Why doesn't this work?
        '& .MuiIconButton-root': { color: palette.ink.main },
      },
    },
  },

  MuiInputLabel: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        fontSize: 13,
        fontWeight: 600,
        color: palette.ink.main,
        '&.Mui-error': { color: 'inherit' },
      },
      shrink: {
        transform: 'translate(2px, -12px)',
        '& + .MuiOutlinedInput-root': {
          marginTop: '12px',
        },
      },
      asterisk: { color: palette.error.main },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        color: palette.primary.dark,
        textDecoration: 'underline',
        textDecorationColor: palette.primary.dark,
        '&:hover, &:focus': {
          color: palette.primary.main,
          textDecoration: 'underline',
          textDecorationColor: palette.primary.main,
        },
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        marginTop: 8,
        marginBottom: 8,
      },
    },
  },

  MuiList: {
    styleOverrides: {
      root: {
        padding: spacing(1),
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        fontSize: 14,
        borderRadius: 10,
        '&:hover': { backgroundColor: '#F3FAF3' },
      },
    },
  },

  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: '0.875rem',
      },
    },
  },

  MuiListSubheader: {
    styleOverrides: {
      root: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 13,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: palette.ink.main,
        marginLeft: 28,

        '.MuiSvgIcon-root': {
          backgroundColor: 'white',
          fontSize: 14,
          marginLeft: -28,
          marginRight: 12,
        },
        '&:hover': {
          // overwrites MuiPopover-paper
          backgroundColor: 'white !important',
          fontWeight: 'bold !important',
          color: `${palette.ink.main} !important`,
        },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 20,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: '9.5px 16px',
        borderRadius: 1,
        fontSize: 14,
        fontWeight: 600,

        '&:hover': { backgroundColor: palette.primary.light },
        '&.Mui-selected': {
          fontWeight: 700,
          color: palette.primary.main,
          backgroundColor: palette.sky.main,
        },
        '.MuiListSubheader-root ~ &': { fontWeight: 400, marginLeft: 28 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        fontSize: 14,
        border: `1px solid ${palette.secondary.light}`,
        borderRadius: 10,

        '&.Mui-error': { border: `1px solid ${palette.error.main}` },
        '&.Mui-focused': {
          '&.MuiInputBase-colorPrimary': {
            border: `1px solid ${palette.primary.main}`,
          },
          '&.MuiInputBase-colorSecondary': {
            border: `1px solid ${palette.secondary.main}`,
          },
          '&.MuiInputBase-colorWarning': {
            border: `1px solid ${palette.warning.main}`,
          },
          '&.MuiInputBase-colorError': {
            border: `1px solid ${palette.error.main}`,
          },
          '&.MuiInputBase-colorInfo': {
            border: `1px solid ${palette.info.main}`,
          },
        },
      },
      input: { padding: '14px' },
      notchedOutline: { display: 'none' },
    },
  },

  MuiPickersArrowSwitcher: {
    styleOverrides: {
      root: {
        '.MuiButtonBase-root': {
          boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.10)',
          height: 24,
          width: 24,
        },
      },
      spacer: {
        width: 36,
      },
    },
  },

  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        backgroundColor: palette.sky.main,
        borderRadius: 100,
        padding: '20px 12px',
        marginRight: 16,
        marginLeft: 16,
      },

      label: {
        fontSize: 13,
      },
      switchViewButton: {
        display: 'none',
      },
    },
  },

  MuiPickersDay: {
    styleOverrides: {
      dayOutsideMonth: {
        color: palette.ink.light,
      },
    },
  },

  MuiPickersLayout: {
    styleOverrides: {
      root: {
        '.MuiDialogActions-root': {
          padding: 8,
          '& button': {
            marginTop: 0,
            marginBottom: 8,
          },
        },
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: 20,
        border: `1px solid ${palette.ink.light}`,
        boxShadow: `0px 4px 14px 0px ${palette.ink.light}`,
        // TBD? Move to MuiMenu-list?
        '& .MuiMenu-list > li': {
          borderRadius: 10,
          '&:hover, &.Mui-focused': {
            backgroundColor: palette.sky.main,
            color: palette.primary.dark,
            fontWeight: 700,
          },
          '&.Mui-selected': {
            backgroundColor: palette.primary.light,
          },
        },
      },
    },
  },

  MuiPopper: {
    styleOverrides: {
      root: {
        '& .MuiAutocomplete-paper, .MuiPickersPopper-paper': {
          borderRadius: 20,
          border: `1px solid ${palette.ink.light}`,
          boxShadow: `0px 4px 14px 0px ${palette.ink.light}`,
        },
        '& .MuiAutocomplete-listbox > li': {
          margin: '0.7rem',
          padding: '0.7rem',
          borderRadius: '0.7rem',
          '&:hover, &.Mui-focused': {
            backgroundColor: palette.primary.light,
          },
          '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': {
            backgroundColor: palette.primary.light,
            color: palette.primary.dark,
            fontWeight: 700,
          },
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        '& .MuiSelect-outlined ~ fieldset.MuiOutlinedInput-notchedOutline': {
          top: 0,
          '& legend': { display: 'none' },
        },
      },
      select: {
        '& .MuiTypography-root': {
          fontSize: 'inherit',
        },
        '&:focus': {
          borderRadius: '0.5rem',
        },
      },
      icon: {
        '.Mui-disable': { color: 'rgba(0, 0, 0, 0.25)' },
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        height: 32,
        width: 50,
        padding: 0,
        '> .Mui-checked + span.MuiSwitch-track': {
          backgroundColor: '#44AB6B',
          opacity: 1,
          border: 0,
        },
        '> .Mui-disabled + span.MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      switchBase: {
        padding: '0 !important',
        margin: 4,
        '&.Mui-checked': {
          left: -2,
          color: 'white',
          transform: 'translateX(20px) !important',
          '&.Mui-disabled': {
            color: 'white',
          },
        },
      },
      track: {
        borderRadius: 20,
      },
      thumb: {
        width: '24px !important',
        height: '24px !important',
        boxShadow: 'none',
      },
    },
  },

  MuiSvgIcon: {
    styleOverrides: {
      root: { fontSize: 18 },
      fontSizeXs: { fontSize: 10 },
      fontSizeSm: { fontSize: 14 },
      fontSizeMd: { fontSize: 18 },
      fontSizeLg: { fontSize: 20 },
      fontSizeXl: { fontSize: 24 },
      colorPrimary: {
        '&.MuiSvgIcon-dark': { color: palette.primary.dark },
        '&.MuiSvgIcon-light': { color: palette.primary.light },
      },
      colorSecondary: {
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': {},
      },
      colorWarning: {
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': {},
      },
      colorError: {
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': { color: palette.error.light },
      },
      colorInfo: {
        color: palette.info.main,
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': {},
      },
      colorInk: {
        color: palette.ink.main,
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': {},
      },
      colorSky: {
        color: palette.sky.main,
        '&.MuiSvgIcon-dark': {},
        '&.MuiSvgIcon-light': {},
      },
      colorGhost: {
        color: palette.ghost.main,
        '&.MuiSvgIcon-dark': { color: palette.ghost.dark },
        '&.MuiSvgIcon-light': { color: palette.ghost.light },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
      },
      toolbar: {
        color: '#808080',
        '& .MuiIconButton-root': {
          color: '#808080 !important',
          '&:disabled': { opacity: 0.4 },
        },
      },
      select: {
        fontSize: '14px',
        border: 'solid 1px #9E9E9E',
        borderRadius: '0.5rem',
        padding: '0.25rem 2rem 0.25rem 0.25rem',
      },
      selectLabel: {
        '& + div': { marginRight: '0.5rem' },
      },
      selectIcon: {
        right: '0.5rem',
        top: 'auto',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 11,
        color: '#E5E5E5',
        backgroundColor: 'rgba(4, 22, 19, 0.88)',
      },
      arrow: {
        color: 'rgba(4, 22, 19, 0.88)',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        fontSize: 14,
        fontWeight: 600,
        color: '#333333',
        textTransform: 'none',
        padding: 0,
        '&.Mui-selected': {
          fontWeight: 700,
          color: '#333333',
        },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 4,
        borderRadius: 2,
        bottom: 8,
      },
    },
  },

  MuiTypography: {
    styleOverrides: {
      gutterBottom: {
        marginBottom: '1em',
      },
      root: {
        a: {
          color: palette.primary.dark,
          textDecoration: 'underline',
          textDecorationColor: palette.primary.dark,
          '&:hover, &:focus': {
            color: palette.primary.main,
            textDecoration: 'underline',
            textDecorationColor: palette.primary.main,
          },
        },
      },
    },
  },
};

export default components;
